import { ReactElement, useCallback } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Stack, Typography } from '@mui/material'
import type { OrgDefaultSettingsDTO } from '@api/fastAPI.schemas'
import {
  getGetOrganisationDefaultSettingsQueryKey,
  useGetOrganisationDefaultSettings,
  useUpdateOrganisationDefaultSettings,
} from '@api/project-settings/project-settings'
import { useQueryClient } from '@tanstack/react-query'
import { LoadingIndicator } from '@ui/feedback'
import { Form2, Switch } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import ManagerSection from '../ManagerSection'
import { configurationSettings } from './settings'

interface Props {
  readOnly: boolean
}

/**
 * Konfiguration (global Systemmanager): the settings the organisation sets for all of its
 * projects. A project can override each of them in its own Systemmanager. Each switch saves on
 * change.
 */
const OrganisationSettingsSection = ({ readOnly }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { data: settings, isLoading } = useGetOrganisationDefaultSettings<
    OrgDefaultSettingsDTO,
    AxiosError
  >({
    query: {
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Konfiguration'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutate: updateSettings } = useUpdateOrganisationDefaultSettings<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Konfiguration speichern',
        description: 'Ändert die Konfiguration der Organisation',
      },
      onSuccess: data => {
        queryClient.setQueryData(getGetOrganisationDefaultSettingsQueryKey(), data)
        enqueueSnackbar('Konfiguration gespeichert', { variant: 'success' })
      },
      onError: async error => {
        await queryClient.invalidateQueries(getGetOrganisationDefaultSettingsQueryKey())
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Konfiguration'), {
          variant: 'error',
        })
      },
    },
  })

  const onSubmit = useCallback(
    (values: OrgDefaultSettingsDTO) => updateSettings({ data: values }),
    [updateSettings],
  )

  return (
    <ManagerSection title="Konfiguration" data-cy="organisation-settings">
      {isLoading || !settings ? (
        <LoadingIndicator />
      ) : (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Gilt für alle Projekte der Organisation, sofern ein Projekt die Einstellung nicht in
            seinem eigenen Systemmanager überschreibt.
          </Typography>
          <Form2<OrgDefaultSettingsDTO>
            id="organisation-settings-form"
            defaultValues={settings}
            reinitialize
            submitOnChange
            onSubmit={onSubmit}
          >
            {configurationSettings.map(setting => (
              <Switch
                key={setting.key}
                name={setting.key}
                label={setting.label}
                size="small"
                disabled={readOnly}
                data-cy={`switch-organisation-setting-${setting.key}`}
              />
            ))}
          </Form2>
        </Stack>
      )}
    </ManagerSection>
  )
}

export default OrganisationSettingsSection
